import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Box,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Avatar,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Person,
  Email,
  Lock,
  Save,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { getAdminProfile, updateAdminProfile, adminLogout } from '../../utils/api';
import AdminLayout from '../../components/AdminLayout';

const ProfileCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const ProfileHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  padding: theme.spacing(3),
  background: '#2C3E50', // Professional dark background
  color: 'white',
  borderRadius: theme.spacing(1),
}));

const AdminProfile = () => {
  const navigate = useNavigate();
  const [adminData, setAdminData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changePassword, setChangePassword] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchAdminProfile();
  }, []);

  const fetchAdminProfile = async () => {
    try {
      setLoading(true);
      const response = await getAdminProfile();
      if (response.data.success) {
        const admin = response.data.admin;
        setAdminData(admin);
        setFormData({
          username: admin.username || '',
          email: admin.email || '',
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        showSnackbar('Failed to load profile data', 'error');
      }
    } catch (error) {
      showSnackbar('Failed to load profile data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordVisibilityToggle = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validateForm = () => {
    if (!formData.username.trim() || !formData.email.trim()) {
      showSnackbar('Username and email are required', 'error');
      return false;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      showSnackbar('Please enter a valid email address', 'error');
      return false;
    }

    if (changePassword) {
      if (!formData.currentPassword) {
        showSnackbar('Current password is required', 'error');
        return false;
      }

      if (!formData.newPassword || formData.newPassword.length < 6) {
        showSnackbar('New password must be at least 6 characters long', 'error');
        return false;
      }

      if (formData.newPassword !== formData.confirmPassword) {
        showSnackbar('New passwords do not match', 'error');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setSaving(true);
      
      const updateData = {
        username: formData.username,
        email: formData.email
      };

      if (changePassword) {
        updateData.currentPassword = formData.currentPassword;
        updateData.newPassword = formData.newPassword;
      }

      const response = await updateAdminProfile(updateData);
      
      if (response.data.success) {
        showSnackbar('Profile updated successfully', 'success');
        
        // Update localStorage with new admin info
        const updatedAdminInfo = response.data.admin;
        localStorage.setItem('adminInfo', JSON.stringify(updatedAdminInfo));
        setAdminData(updatedAdminInfo);
        
        // Reset password fields
        setFormData(prev => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        }));
        setChangePassword(false);
      } else {
        showSnackbar(response.data.message || 'Failed to update profile', 'error');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update profile';
      showSnackbar(errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await adminLogout();
      localStorage.removeItem('adminLoggedIn');
      localStorage.removeItem('adminInfo');
      navigate('/admin/login');
    } catch (error) {
      // Force logout even if API call fails
      localStorage.removeItem('adminLoggedIn');
      localStorage.removeItem('adminInfo');
      navigate('/admin/login');
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Admin Profile">
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={60} />
          </Box>
        </Container>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Admin Profile">
      <Container maxWidth="md">
        {/* Profile Header */}
        <ProfileHeader>
          <Avatar 
            sx={{ 
              bgcolor: 'rgba(255, 255, 255, 0.2)', 
              mr: 3, 
              width: 80, 
              height: 80,
              fontSize: '2rem'
            }}
          >
            {adminData?.username?.charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
              {adminData?.username}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              {adminData?.email}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              Member since {new Date(adminData?.created_at).toLocaleDateString()}
            </Typography>
          </Box>
        </ProfileHeader>

        {/* Profile Form */}
        <ProfileCard>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Profile Settings
            </Typography>

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Basic Information */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom color="primary">
                    Basic Information
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Username"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    InputProps={{
                      startAdornment: <Person sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Email Address"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    InputProps={{
                      startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                </Grid>

                {/* Password Section */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6" color="primary">
                      Password Settings
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={changePassword}
                          onChange={(e) => setChangePassword(e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Change Password"
                    />
                  </Box>
                </Grid>

                {changePassword && (
                  <>
                    <Grid item xs={12}>
                      <TextField
                        label="Current Password"
                        name="currentPassword"
                        type={showPasswords.current ? 'text' : 'password'}
                        value={formData.currentPassword}
                        onChange={handleInputChange}
                        fullWidth
                        required={changePassword}
                        InputProps={{
                          startAdornment: <Lock sx={{ mr: 1, color: 'text.secondary' }} />,
                          endAdornment: (
                            <Button
                              onClick={() => handlePasswordVisibilityToggle('current')}
                              sx={{ minWidth: 'auto', p: 1 }}
                            >
                              {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                            </Button>
                          )
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="New Password"
                        name="newPassword"
                        type={showPasswords.new ? 'text' : 'password'}
                        value={formData.newPassword}
                        onChange={handleInputChange}
                        fullWidth
                        required={changePassword}
                        helperText="At least 6 characters"
                        InputProps={{
                          startAdornment: <Lock sx={{ mr: 1, color: 'text.secondary' }} />,
                          endAdornment: (
                            <Button
                              onClick={() => handlePasswordVisibilityToggle('new')}
                              sx={{ minWidth: 'auto', p: 1 }}
                            >
                              {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                            </Button>
                          )
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Confirm New Password"
                        name="confirmPassword"
                        type={showPasswords.confirm ? 'text' : 'password'}
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        fullWidth
                        required={changePassword}
                        InputProps={{
                          startAdornment: <Lock sx={{ mr: 1, color: 'text.secondary' }} />,
                          endAdornment: (
                            <Button
                              onClick={() => handlePasswordVisibilityToggle('confirm')}
                              sx={{ minWidth: 'auto', p: 1 }}
                            >
                              {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                            </Button>
                          )
                        }}
                      />
                    </Grid>
                  </>
                )}

                {/* Submit Button */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={saving}
                      startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => {
                        setFormData({
                          username: adminData?.username || '',
                          email: adminData?.email || '',
                          currentPassword: '',
                          newPassword: '',
                          confirmPassword: ''
                        });
                        setChangePassword(false);
                      }}
                    >
                      Reset
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </ProfileCard>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </AdminLayout>
  );
};

export default AdminProfile;
